import { Booking, Resource, WorkingHoursProfile, Absence, BankHoliday } from '@/types';
import { calculatePeriodUtilization } from '@/utils/heatmap-utils';

/**
 * Calculate resource utilization percentage for a given time period.
 * Delegates to calculatePeriodUtilization (the canonical implementation)
 * in heatmap-utils.ts. The function signature is kept for backward
 * compatibility with existing callers.
 */
export function calculateResourceUtilization(
  resourceId: string,
  bookings: Booking[],
  startDate: Date,
  endDate: Date,
  resource?: Resource,
  globalWorkingProfile?: WorkingHoursProfile,
  _absences?: Absence[],
  _bankHolidays?: BankHoliday[]
): number {
  if (!resource || !globalWorkingProfile) {
    return 0;
  }

  // Normalise inverted / equal date ranges (e.g. day view)
  const periodStart = startDate <= endDate ? startDate : endDate;
  const periodEnd   = startDate <= endDate ? endDate   : startDate;

  const { utilizationPercentage } = calculatePeriodUtilization(
    String(resourceId),
    periodStart,
    periodEnd,
    bookings,
    resource,
    globalWorkingProfile
  );

  return utilizationPercentage;
}

/**
 * Generate a consistent color for a project name
 */
export function getProjectColor(projectName: string): string {
  const colors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
    '#14b8a6', // teal
    '#a855f7', // violet
    '#84cc16', // lime
    '#f43f5e', // rose
  ];
  
  let hash = 0;
  for (let i = 0; i < projectName.length; i++) {
    hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

/**
 * Check if two bookings overlap
 */
export function checkBookingOverlap(
  booking1: { startTime: Date | string; endTime: Date | string },
  booking2: { startTime: Date | string; endTime: Date | string }
): boolean {
  const start1 = new Date(booking1.startTime);
  const end1 = new Date(booking1.endTime);
  const start2 = new Date(booking2.startTime);
  const end2 = new Date(booking2.endTime);

  return start1 < end2 && start2 < end1;
}

/**
 * Format duration in a human-readable way
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Generate time slots for booking forms
 */
export function generateTimeSlots(interval: number = 30): Array<{ hour: number; minute: number; label: string }> {
  const slots = [];
  
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const hourStr = hour.toString().padStart(2, '0');
      const minuteStr = minute.toString().padStart(2, '0');
      const period = hour < 12 ? 'AM' : 'PM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      
      slots.push({
        hour,
        minute,
        label: `${displayHour}:${minuteStr} ${period}`,
      });
    }
  }
  
  return slots;
}
